import { logger, config } from '@/common/services/config';

/**
 * Storage service that provides a fallback from localStorage to sessionStorage
 * when localStorage is not available (e.g., in private browsing mode)
 */
class StorageService {
  private storage: Storage;

  constructor() {
    this.storage = this.getAvailableStorage();
  }

  private getAvailableStorage(): Storage {
    try {
      // Test localStorage availability
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      logger.log('Using localStorage as primary storage');
      return localStorage;
    } catch (error) {
      logger.warn('localStorage not available, falling back to sessionStorage:');
      try {
        // Test sessionStorage availability
        const testKey = '__storage_test__';
        sessionStorage.setItem(testKey, 'test');
        sessionStorage.removeItem(testKey);
        logger.log('Using sessionStorage as fallback storage');
        return sessionStorage;
      } catch (sessionError) {
        logger.error('Both localStorage and sessionStorage are not available:');
        // Return a mock storage object that doesn't persist data
        return this.createMockStorage();
      }
    }
  }

  private createMockStorage(): Storage {
    const mockStorage: { [key: string]: string } = {};
    return {
      getItem: (key: string) => mockStorage[key] || null,
      setItem: (key: string, value: string) => {
        mockStorage[key] = value;
      },
      removeItem: (key: string) => {
        delete mockStorage[key];
      },
      clear: () => {
        Object.keys(mockStorage).forEach((key) => delete mockStorage[key]);
      },
      key: (index: number) => Object.keys(mockStorage)[index] || null,
      get length() {
        return Object.keys(mockStorage).length;
      },
    } as Storage;
  }

  /**
   * Get an item from storage
   */
  getItem(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch (error) {
      logger.error(`Error getting item '${key}' from storage:`, error);
      return null;
    }
  }

  /**
   * Set an item in storage
   */
  setItem(key: string, value: string): boolean {
    try {
      this.storage.setItem(key, value);
      return true;
    } catch (error) {
      logger.error(`Error setting item '${key}' in storage:`, error);
      return false;
    }
  }

  /**
   * Remove an item from storage
   */
  removeItem(key: string): boolean {
    try {
      this.storage.removeItem(key);
      return true;
    } catch (error) {
      logger.error(`Error removing item '${key}' from storage:`, error);
      return false;
    }
  }

  /**
   * Clear all items from storage
   */
  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      logger.error('Error clearing storage:', error);
    }
  }

  /**
   * Get the current storage type being used
   */
  getStorageType(): string {
    if (this.storage === localStorage) return 'localStorage';
    if (this.storage === sessionStorage) return 'sessionStorage';
    return 'mockStorage';
  }

  /**
   * Check if storage is available
   */
  isAvailable(): boolean {
    return this.storage !== null;
  }

  /**
   * Migrate data from localStorage to current storage if needed
   * This is useful when localStorage becomes unavailable and we fallback to sessionStorage
   */
  migrateFromLocalStorage(): void {
    // Only migrate if we're not using localStorage and localStorage is available
    if (this.storage === localStorage) return;

    try {
      const keysToMigrate = ['access_token', 'theme', 'ms_token'];

      for (const key of keysToMigrate) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          this.setItem(key, value);
          logger.log(`Migrated ${key} from localStorage to ${this.getStorageType()}`);
        }
      }
    } catch (error) {
      logger.warn('Failed to migrate data from localStorage:', error);
    }
  }

  /**
   * Get all items from storage (for debugging purposes)
   */
  getAllItems(): { [key: string]: string | null } {
    const items: { [key: string]: string | null } = {};
    try {
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key) {
          items[key] = this.getItem(key);
        }
      }
    } catch (error) {
      logger.error('Error getting all storage items:', error);
    }
    return items;
  }
}

// Create and export a singleton instance
export const storageService = new StorageService();

// Expose storage service to global scope for debugging (only when debug mode is enabled)
if (typeof window !== 'undefined' && config.features.enableDebug) {
  (window as any).debugStorage = {
    getStorageType: () => storageService.getStorageType(),
    isAvailable: () => storageService.isAvailable(),
    getAllItems: () => storageService.getAllItems(),
    storageService,
  };
}

// Export the class for testing purposes
export { StorageService };
